// We use Tailwind CSS for styling.
import React, { useMemo, useState, useCallback } from "react";

// Removed: import Swal from "sweetalert2"; // Cannot be resolved in this environment

// --- REAL IMPORTS (FOR YOUR LOCAL ENVIRONMENT) ---
// If you run this application in your local environment, ensure these lines
// are UNCOMMENTED and that '../lib/api' points to your real fetch/axios function.
import { Link, useNavigate } from "react-router-dom"; // NOTE: These are now handled via mock/<a> to avoid environment errors
import { api } from "../lib/api"; // <-- Use your actual API function here

// API Response Types
type RegisterResponse = {
  token?: string;
  Token?: string;
  message?: string;
};

// Base Form Structure
type FormState = {
  username: string;
  email: string;
  password: string;
  confirm: string;
  hotelName: string;
  accept: boolean;
};

// Field Error Types
type FieldErrors = {
  [K in keyof FormState]?: string;
};

// --- INLINE SVG ICONS ---
const EyeIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const EyeOffIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
    <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.48-1.63" />
    <line x1="2" x2="22" y1="2" y2="22" />
  </svg>
);
const CheckIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);
// --- END SVG ICONS ---

// Calculates password complexity score
const calculatePasswordScore = (p: string): number => {
  let s = 0;
  if (p.length >= 8) s++;
  if (/[A-Z]/.test(p)) s++;
  if (/[a-z]/.test(p)) s++;
  if (/\d/.test(p)) s++;
  if (/[^A-Za-z0-9]/.test(p)) s++;
  return s;
};

// Base Input Component
function Input({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  required,
  errorMsg,
  onBlur,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  errorMsg?: string | null;
  onBlur?: () => void;
}) {
  const isError = !!errorMsg;
  const borderColor = isError
    ? "border-red-500"
    : "border-gray-300 focus:border-blue-500";

  return (
    <label className="block mb-4">
      <span className="block font-medium mb-1.5 text-gray-700 text-sm">
        {label} {required && <span className="text-red-500">*</span>}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        required={required}
        className={`w-full px-4 py-2.5 border ${borderColor} rounded-lg outline-none text-sm transition duration-150 shadow-sm`}
      />
      {isError && (
        <p className="text-red-500 text-xs mt-1.5 flex items-center">
          {errorMsg}
        </p>
      )}
    </label>
  );
}

// Password Field Component with Strength Indicator
function PasswordField({
  label,
  value,
  onChange,
  show,
  toggle,
  pwdScore,
  pwdLabel,
  errorMsg,
  onBlur,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  show: boolean;
  toggle: () => void;
  pwdScore: number;
  pwdLabel: string;
  errorMsg?: string | null;
  onBlur?: () => void;
}) {
  const isError = !!errorMsg;
  const borderColor = isError
    ? "border-red-500"
    : "border-gray-300 focus:border-blue-500";
  // Progressive colors for password strength bar
  const progressColor = [
    "bg-red-500",
    "bg-orange-500",
    "bg-yellow-500",
    "bg-green-500",
    "bg-lime-600",
    "bg-emerald-600",
  ];
  const progressBarColor =
    progressColor[Math.max(0, Math.min(5, pwdScore - 1))];

  return (
    <div className="mb-4">
      <span className="block font-medium mb-1.5 text-gray-700 text-sm">
        {label} <span className="text-red-500">*</span>
      </span>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          required
          className={`w-full px-4 py-2.5 border ${borderColor} rounded-lg outline-none text-sm pr-12 transition duration-150 shadow-sm`}
        />
        <button
          type="button"
          onClick={toggle}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-blue-600 transition duration-150 rounded-md"
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show ? (
            <EyeOffIcon className="w-5 h-5" />
          ) : (
            <EyeIcon className="w-5 h-5" />
          )}
        </button>
      </div>

      {value && (
        <div className="mt-1">
          <div className="h-1.5 rounded-full bg-gray-200 overflow-hidden mb-1">
            <div
              className={`${progressBarColor} h-full transition-all duration-300`}
              style={{ width: `${(pwdScore / 5) * 100}%` }}
            />
          </div>
          <small className="text-gray-500 text-xs">Strength: {pwdLabel}</small>
        </div>
      )}

      {isError && (
        <p className="text-red-500 text-xs mt-1.5 flex items-center">
          {errorMsg}
        </p>
      )}
    </div>
  );
}

// Success MODAL Component
function SuccessModal({
  navigate,
  email,
}: {
  // navigate prop type must match the function being passed
  navigate: (path: string, options?: { replace?: boolean }) => void;
  email: string;
}) {
  // Use the navigate function passed as prop
  const handleNavigate = () => {
    // Navigate to Dashboard on click (using the mock or real navigate function)
    navigate("/dashboard", { replace: true });
  };

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
      <div className="bg-white p-8 md:p-10 rounded-xl shadow-2xl w-full max-w-sm transform transition-all duration-300 scale-100">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 text-green-600 mb-6 border-4 border-green-200 animate-pulse-once">
            <CheckIcon className="h-8 w-8" />
          </div>
          <h3 className="text-2xl font-semibold text-gray-900 mb-2">
            Registration Successful!
          </h3>
          <p className="text-base text-gray-700 mb-4">Welcome to HotelVPF!</p>
          <p className="text-sm text-gray-500 mb-8 leading-relaxed">
            Your account, registered with the email{" "}
            <span className="font-semibold text-gray-900">{email}</span>, is now
            active. Click below to access the Dashboard.
          </p>
          <button
            onClick={handleNavigate}
            className="w-full py-3 px-4 rounded-lg font-semibold text-white bg-blue-600 hover:bg-blue-700 transition duration-300 shadow-md hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-blue-300"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Register() {
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>({
    username: "",
    email: "",
    password: "",
    confirm: "",
    hotelName: "",
    accept: false,
  });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const [showSuccess, setShowSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState<string>("");

  // Calculates password strength score
  const pwdScore = useMemo(
    () => calculatePasswordScore(form.password),
    [form.password]
  );
  const pwdLabel = [
    "Very Weak",
    "Weak",
    "Medium",
    "Good",
    "Strong",
    "Excellent",
  ][Math.min(pwdScore, 5)];

  // Field validation logic
  const validateField = useCallback(
    (name: keyof FormState, value: string | boolean) => {
      let error: string | undefined = undefined;

      switch (name) {
        case "username":
          if (!value) error = "Username is required.";
          else if (String(value).trim().length < 3)
            error = "Username must be at least 3 characters long.";
          break;
        case "email":
          if (!value) error = "Email address is required.";
          else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value)))
            error = "Invalid email format.";
          break;
        case "password":
          if (!value) error = "Password is required.";
          else if (pwdScore < 3)
            error =
              "Password is too weak. Requires min 8 characters, letters, numbers, and symbols.";
          break;
        case "confirm":
          if (!value) error = "Password confirmation is required.";
          else if (String(value) !== form.password)
            error = "Passwords do not match.";
          break;
        case "accept":
          if (!value) error = "You must accept the Terms and Conditions.";
          break;
        case "hotelName":
        default:
          break;
      }

      // Set or clear the error
      setFieldErrors((prev) => {
        if (error) {
          return { ...prev, [name]: error };
        } else {
          // Fix: Delete the key, not just the value
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        }
      });

      return error;
    },
    [form.password, pwdScore]
  );

  // Run validation for all fields on submit
  const validateForm = useCallback(() => {
    const fieldsToValidate: (keyof FormState)[] = [
      "username",
      "email",
      "password",
      "confirm",
      "accept",
    ];
    let hasError = false;
    let newErrors: FieldErrors = {};

    fieldsToValidate.forEach((name) => {
      const error = validateField(name, form[name]);
      if (error) {
        newErrors[name] = error;
        hasError = true;
      }
    });

    // Update the error state once
    setFieldErrors(newErrors);
    return !hasError;
  }, [form, validateField]);

  // General change handler
  const handleChange = (name: keyof FormState) => (value: string | boolean) => {
    setForm((prev) => {
      const newForm = { ...prev, [name]: value as any };

      // Re-validate "Confirm Password" if Password changes
      if (name === "password" && newForm.confirm) {
        validateField("confirm", newForm.confirm);
      }

      return newForm;
    });

    if (apiError) setApiError(null);

    // Re-validate immediately if an error is already displayed
    if (fieldErrors[name]) {
      validateField(name, value);
    }
  };

  // Handler for blur (feedback on leaving the field)
  const handleBlur = (name: keyof FormState) => () => {
    validateField(name, form[name]);
  };

  // General submission condition
  const canSubmit = useMemo(() => {
    // Checks if all required fields are filled and valid.
    const hasFieldErrors = Object.keys(fieldErrors).length > 0;

    return (
      form.username.trim().length >= 3 &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) &&
      pwdScore >= 3 &&
      form.password === form.confirm &&
      form.accept &&
      !loading &&
      !hasFieldErrors
    );
  }, [form, pwdScore, loading, fieldErrors]); // Include fieldErrors in dependencies

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Ensure full validation runs before proceeding
    if (!validateForm() || !canSubmit) return;

    setApiError(null);
    setLoading(true);

    try {
      const formData = {
        username: form.username,
        email: form.email,
        hotelName: form.hotelName,
        password: form.password,
        confirmPassword: form.confirm,
      };

      // Assuming 'api' throws an error for HTTP 4xx/5xx status codes
      const res: RegisterResponse = await api("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      // Check if a token exists in the response (success)
      if (res.token || res.Token) {
        const token = (res.token || res.Token) as string;
        localStorage.setItem("token", token);

        // Success: Use custom modal
        setRegisteredEmail(form.email);
        setShowSuccess(true);
      } else if (res.message) {
        // Case where success is signaled but needs user action (e.g., email verification)
        setApiError(res.message);
      } else {
        throw new Error(
          "Registration successful, but unexpected API response format."
        );
      }
    } catch (err: any) {
      console.error("API Error:", err);
      const errorMessage = err.message || "Unknown error. Please try again.";
      // Use the in-app error box to show the message
      setApiError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Display the success modal if registration was successful
  if (showSuccess) {
    return <SuccessModal navigate={navigate} email={registeredEmail} />;
  }

  return (
    <div className="min-h-screen flex justify-center items-center bg-[#f9fafb] p-4 font-sans antialiased">
      {/* Card Container */}
      <div className="w-full max-w-md bg-white p-12 rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-all duration-300 border border-gray-200">
        <div className="text-center mb-8">
          {/* Placeholder Logo */}
          {/* React Logo identical to Login */}
          <img
            src="logos/hotelvpf-logo.png"
            style={{ width: 90, height: "auto", marginBottom: 8 }}
            loading="lazy"
            alt="HotelVPF Logo"
            className="h-16 mx-auto mb-3"
          />

          {/* Title - Thinner and compact font */}
          <h1 className="text-[22px] font-medium text-[#111827] mb-2">
            Create your HotelVPF account
          </h1>
          <p className="text-[13px] text-[#2563eb] font-medium mb-1">
            Join today and get{" "}
            <span className="font-semibold">1 month free access</span> to the
            HotelVPF Dashboard.
          </p>
          <p className="text-[13px] text-[#6b7280] mb-2">
            Trusted by hotels worldwide 🌍
          </p>
          <p className="text-[14px] text-[#6b7280]">
            Access your property dashboard and manage reservations securely.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* API Error Box */}
          {apiError && (
            <div className="bg-red-50 border border-red-300 text-red-800 p-3 rounded-lg mb-4 text-sm font-medium transition duration-300">
              {apiError}
            </div>
          )}

          {/* Form Fields */}
          <Input
            label="Username"
            value={form.username}
            onChange={handleChange("username")}
            onBlur={handleBlur("username")}
            errorMsg={fieldErrors.username}
            required
            placeholder="Ex: john_smith"
          />
          <Input
            label="Email"
            type="email"
            value={form.email}
            onChange={handleChange("email")}
            onBlur={handleBlur("email")}
            errorMsg={fieldErrors.email}
            required
            placeholder="Ex: contact@myhotel.com"
          />
          <Input
            label="Hotel / Business Name (Optional)"
            value={form.hotelName}
            onChange={handleChange("hotelName")}
            placeholder="Ex: The Grand Plaza"
          />

          <PasswordField
            label="Password"
            value={form.password}
            onChange={handleChange("password")}
            onBlur={handleBlur("password")}
            show={showPwd}
            toggle={() => setShowPwd((v) => !v)}
            pwdScore={pwdScore}
            pwdLabel={pwdLabel}
            errorMsg={fieldErrors.password}
          />

          <Input
            label="Confirm Password"
            type="password"
            value={form.confirm}
            onChange={handleChange("confirm")}
            onBlur={handleBlur("confirm")}
            errorMsg={fieldErrors.confirm}
            required
          />

          {/* Terms and Conditions Checkbox */}

          <label className="flex items-start gap-3 mt-4 mb-6 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.accept}
              onChange={(e) => handleChange("accept")(e.target.checked)}
              onBlur={handleBlur("accept")}
              className={`mt-1.5 h-4 w-4 rounded text-blue-600 border ${
                fieldErrors.accept ? "border-red-500" : "border-gray-300"
              } focus:ring-blue-500 transition duration-150`}
            />
            <div className="text-sm text-gray-600">
              I agree to the{" "}
              <a
                href="#"
                style={{
                  color: "#2563eb",
                  fontWeight: 400,
                  textDecoration: "none",
                  textUnderlineOffset: "2px",
                  transition: "0.2s",
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.textDecoration = "underline")
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.textDecoration = "none")
                }
                onClick={(e) => e.preventDefault()}
              >
                Terms and Conditions
              </a>{" "}
              and{" "}
              <a
                href="#"
                style={{
                  color: "#2563eb",
                  fontWeight: 400,
                  textDecoration: "none",
                  textUnderlineOffset: "2px",
                  transition: "0.2s",
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.textDecoration = "underline")
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.textDecoration = "none")
                }
                onClick={(e) => e.preventDefault()}
              >
                Privacy Policy
              </a>
              .
              {fieldErrors.accept && (
                <p className="text-red-500 text-xs mt-1.5 flex items-center">
                  {fieldErrors.accept}
                </p>
              )}
            </div>
          </label>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!canSubmit || loading}
            className={`w-full py-3.5 px-4 rounded-lg font-bold text-white transition duration-300 shadow-lg ${
              canSubmit && !loading
                ? "bg-blue-600 hover:bg-blue-700 cursor-pointer transform hover:scale-[1.01] focus:ring-4 focus:ring-blue-300"
                : "bg-gray-400 cursor-not-allowed opacity-80"
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Creating account...
              </span>
            ) : (
              "Create Account"
            )}
          </button>

          {/* Login Link */}

          <div style={{ marginTop: 28, textAlign: "center" }}>
            <span style={{ color: "#6b7280", fontSize: 14 }}>
              Already have an account?{" "}
            </span>
            <a
              href="#"
              style={{
                color: "#2563eb",
                fontSize: 14,
                fontWeight: 400,
                textDecoration: "none",
                textUnderlineOffset: "2px",
                transition: "0.2s",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.textDecoration = "underline")
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.textDecoration = "none")
              }
              onClick={(e) => {
                e.preventDefault();
                navigate("/login");
              }}
            >
              Sign in →
            </a>
          </div>
        </form>
      </div>

      {/* Tailwind CSS Script and Font Import */}
      <script src="https://cdn.tailwindcss.com"></script>
      <style>
        {`
          /* Use Inter font, which is compact */
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

          .font-sans {
              font-family: 'Inter', sans-serif !important;
          }

          /* Ensure success animation runs only once */
          @keyframes pulse-once {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.8; }
          }
          .animate-pulse-once {
              animation: pulse-once 1.5s ease-in-out 1;
          }
        `}
      </style>
    </div>
  );
}
